/**
 * The own outgoing mail server of a scope — garage or garage group.
 *
 * The scope number never travels: the cockpit facade fills it from the user scope,
 * the scope is chosen by the endpoint. Saving writes a *candidate*, not the row that
 * sends — it takes effect only once the code from a mail the new server itself
 * delivered has been entered. That is the whole point of the route: a wrong setting
 * cannot cut a garage off from its mails.
 */

/** The one choice the form offers; the BFF turns it into the two flags it stores. */
export type MailServerEncryption = "NONE" | "STARTTLS" | "SSL";

export type MailTransportType = "SMTP" | "OFFICE365";

/** Set on a row of that transport, absent on any other. */
export interface SmtpDetails {
    server?: string | null;
    port?: number | null;
    user?: string | null;
    password?: string | null;
    encryption?: MailServerEncryption | null;
}

export interface Office365Details {
    tenantId?: string | null;
    clientId?: string | null;
    clientSecretSet?: boolean;
}

/** Where the mails of this scope actually go out from today. */
export type MailSenderOrigin = "OWN" | "GARAGE_GROUP" | "PLATFORM";

/** Where a failure came from - a probe and a lost order confirmation read differently. */
export type MailServerFailureSource = "HEALTH_CHECK" | "TEST_MAIL" | "SEND";

export const failureSourceLabelKeys: Record<MailServerFailureSource, string> = {
    HEALTH_CHECK: "settings.mailServer.failureFromCheck",
    TEST_MAIL: "settings.mailServer.failureFromTestMail",
    SEND: "settings.mailServer.failureFromSend",
};

/** The outcome of a connection attempt, from the health check or a sent code. */
export type MailServerCheckStatus =
    | "NO_ERROR"
    | "AUTH_ERROR"
    | "MESSAGING_ERROR"
    | "OTHER_ERROR"
    | "MAIL_TOO_LARGE"
    | "SECRET_EXPIRED"
    | "SECRET_INVALID"
    | "APP_NOT_FOUND"
    | "TENANT_NOT_FOUND"
    | "ACCESS_DENIED"
    | "MAILBOX_NOT_FOUND";

/**
 * Trying again with the very same values only helps where the dealer fixed something
 * at Microsoft. A wrong secret or an unknown tenant needs the field corrected first,
 * so there the page points at the field instead of offering the retry.
 */
export const isRepairableWithoutEditing = (status?: MailServerCheckStatus | null): boolean =>
    status === "ACCESS_DENIED"
    || status === "MAILBOX_NOT_FOUND"
    || status === "AUTH_ERROR"
    || status === "MESSAGING_ERROR";

export type MailServerActivationResult =
    | "ACTIVATED"
    | "NO_CANDIDATE"
    | "INVALID_CODE"
    | "EXPIRED"
    | "TOO_MANY_ATTEMPTS";

/**
 * One configuration as the backend hands it out — the active row or a candidate.
 * `server` empty means there is none; the password is masked and can never be read
 * back, so an untouched field must be sent empty to mean "unchanged".
 *
 * Every unset field arrives as an explicit `null`, not as an absent one: the backend
 * serialises the whole record. Hence `| null` throughout — a bare `=== undefined`
 * check would let a null through and break on the value behind it.
 */
export interface MailServerDetails {
    transport?: MailTransportType | null;
    smtp?: SmtpDetails | null;
    office365?: Office365Details | null;
    senderMailAddress?: string | null;
    senderName?: string | null;
    credentialsRequired?: boolean;

    /** Health of the active row. False with a failure means: taken out of the send path. */
    verified?: boolean;
    consecutiveCheckFailures?: number;
    failuresUntilDeactivation?: number;
    lastSuccessAt?: string | null;
    lastFailureAt?: string | null;
    lastFailureStatus?: MailServerCheckStatus | null;
    lastFailureSource?: MailServerFailureSource | null;

    /** What the other side said when it last failed. */
    lastFailureDetail?: string | null;

    /**
     * Candidate only: a code has been sent and has not expired, so the entry of that
     * code survives a page reload.
     */
    activationCodePending?: boolean;
    activationCodeValidityHours?: number;

    /** What sends today — set on the active row, never on a candidate. */
    effectiveSenderMailAddress?: string | null;
    effectiveSenderOrigin?: MailSenderOrigin | null;
}

export interface SaveSmtpSettings {
    host?: string;
    port?: number;
    username?: string;
    /** Empty means unchanged — the stored one is masked on the way out. */
    password?: string;
    encryption?: MailServerEncryption;
}

export interface SaveOffice365Settings {
    tenantId?: string;
    clientId?: string;
    /** Empty means unchanged — the stored one never leaves the service. */
    clientSecret?: string;
}

export interface SaveMailServerRequest {
    transport?: MailTransportType;
    smtp?: SaveSmtpSettings;
    office365?: SaveOffice365Settings;
    senderMailAddress?: string;
    senderName?: string;
    /** Drops a pending change instead of saving one. */
    shouldDelete?: boolean;
}

export interface SendActivationMailRequest {
    receiver: string;
}

export interface SendTestMailRequest {
    receiver: string;
}

export interface ActivateMailServerRequest {
    activationCode: string;
}

export interface MailServerTrialResponse {
    type: MailServerCheckStatus;
    /** What the other side said, for someone who does not see our logs. */
    detail?: string | null;
}

export interface MailServerActivationResponse {
    type: MailServerActivationResult;
    remainingAttempts: number;
}

/**
 * A row that exists at all — an empty answer carries no transport. Going by the host
 * would read every Office 365 row as "not set up": it has none.
 */
export const isConfigured = (details?: MailServerDetails): boolean =>
    details?.transport === "SMTP"
        ? (details?.smtp?.server ?? "").length > 0
        : (details?.office365?.tenantId ?? "").length > 0;

/**
 * A configuration the health check has switched off. Only says so once a check has
 * actually run — a row is verified from the moment it is activated.
 */
export const isDeactivated = (details?: MailServerDetails): boolean =>
    isConfigured(details) && details?.verified === false;

export const isFailing = (details?: MailServerDetails): boolean =>
    isConfigured(details)
    && details?.verified === true
    && (details?.consecutiveCheckFailures ?? 0) > 0;

export type MailServerHealth = "UNKNOWN" | "NONE" | "OK" | "FAILING" | "DEACTIVATED";

export const healthOf = (details?: MailServerDetails): MailServerHealth => {
    if (!isConfigured(details)) {
        return "NONE";
    }
    if (isDeactivated(details)) {
        return "DEACTIVATED";
    }
    return isFailing(details) ? "FAILING" : "OK";
};

export const needsAttention = (health: MailServerHealth): boolean =>
    health === "FAILING" || health === "DEACTIVATED";

export type MailServerRejectionReason =
    | "NEEDS_USERNAME"
    | "NEEDS_PASSWORD"
    | "NEEDS_ENCRYPTION"
    | "NEEDS_HOST"
    | "NEEDS_PORT"
    | "NEEDS_TENANT_ID"
    | "INVALID_TENANT_ID"
    | "NEEDS_CLIENT_ID"
    | "NEEDS_CLIENT_SECRET"
    | "NO_CANDIDATE"
    | "NOTHING_TO_RETRY"
    | "NO_VERIFIED_SERVER"
    | "NO_SENDING_SERVER";

export const rejectionLabelKeys: Record<MailServerRejectionReason, string> = {
    NEEDS_USERNAME: "settings.mailServer.rejectedNeedsUsername",
    NEEDS_PASSWORD: "settings.mailServer.rejectedNeedsPassword",
    NEEDS_ENCRYPTION: "settings.mailServer.rejectedNeedsEncryption",
    NEEDS_HOST: "settings.mailServer.rejectedNeedsHost",
    NEEDS_PORT: "settings.mailServer.rejectedNeedsPort",
    NEEDS_TENANT_ID: "settings.mailServer.rejectedNeedsTenantId",
    INVALID_TENANT_ID: "settings.mailServer.rejectedInvalidTenantId",
    NEEDS_CLIENT_ID: "settings.mailServer.rejectedNeedsClientId",
    NEEDS_CLIENT_SECRET: "settings.mailServer.rejectedNeedsClientSecret",
    NO_CANDIDATE: "settings.mailServer.rejectedNoCandidate",
    NOTHING_TO_RETRY: "settings.mailServer.rejectedNothingToRetry",
    NO_VERIFIED_SERVER: "settings.mailServer.rejectedNoVerifiedServer",
    NO_SENDING_SERVER: "settings.mailServer.rejectedNoSendingServer",
};

export const rejectionReasonOf = (error: unknown): MailServerRejectionReason | undefined => {
    const data = (error as {response?: {data?: unknown}} | undefined)?.response?.data;
    const reason = (data as {reason?: unknown} | undefined)?.reason;
    return typeof reason === "string" && reason in rejectionLabelKeys
        ? reason as MailServerRejectionReason
        : undefined;
};

export const checkStatusLabelKeys: Record<MailServerCheckStatus, string> = {
    NO_ERROR: "settings.mailServer.statusReasonNone",
    AUTH_ERROR: "settings.mailServer.statusReasonAuth",
    MESSAGING_ERROR: "settings.mailServer.statusReasonMessaging",
    OTHER_ERROR: "settings.mailServer.statusReasonOther",
    MAIL_TOO_LARGE: "settings.mailServer.statusReasonMailTooLarge",
    SECRET_EXPIRED: "settings.mailServer.statusReasonSecretExpired",
    SECRET_INVALID: "settings.mailServer.statusReasonSecretInvalid",
    APP_NOT_FOUND: "settings.mailServer.statusReasonAppNotFound",
    TENANT_NOT_FOUND: "settings.mailServer.statusReasonTenantNotFound",
    ACCESS_DENIED: "settings.mailServer.statusReasonAccessDenied",
    MAILBOX_NOT_FOUND: "settings.mailServer.statusReasonMailboxNotFound",
};

export const activationResultLabelKeys: Record<MailServerActivationResult, string> = {
    ACTIVATED: "settings.mailServer.activationActivated",
    NO_CANDIDATE: "settings.mailServer.activationNoCandidate",
    INVALID_CODE: "settings.mailServer.activationInvalidCode",
    EXPIRED: "settings.mailServer.activationExpired",
    TOO_MANY_ATTEMPTS: "settings.mailServer.activationTooManyAttempts",
};
